import {
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"

import { LabelColor } from "@/v2/graphql/public-generated-rq"
import { Button, FormField, Input, Tag } from "@lumen-energy/design-system"
import { IconX } from "@tabler/icons-react"

import { ExistingLabelsList } from "./ExistingLabelsList"
import { Label, LabelProps } from "./Label"
import {
  LabelGroupProps,
  LabelThemeColors,
  TempLabelsProps,
  retrieveColorKey,
} from "./LabelGroup"
import { SelectLabelColor, radioItemDisplayColors } from "./SelectLabelColor"

export interface AddLabelPopoverContentProps
  extends Pick<LabelGroupProps, "orgLabels" | "editLabelColor" | "editLabelName"> {
  handleSendBatchedUpdate: () => void
  helperText?: string
  deleteLabel?: (siteLabelPid: string) => void
  // Remove label function may remove a single label, or may remove multiple labels (for Bulk Actions)
  removeLabel?: (labelPid: string, labelName: string) => void
  isBulkAction?: boolean
}

export function AddLabelPopoverContent({
  handleSendBatchedUpdate,
  helperText,
  orgLabels,
  tempLabels,
  setTempLabels,
  editLabelColor,
  editLabelName,
  deleteLabel = () => {},
  removeLabel,
  isBulkAction,
}: AddLabelPopoverContentProps & TempLabelsProps) {
  const hasSiteLabels = tempLabels?.length > 0
  const hasOrgLabels = orgLabels.length > 0
  const hasNoLabels = !hasSiteLabels && !hasOrgLabels

  const defaultSelectedColor = LabelThemeColors.SUCCESS
  const [newLabelPreviewText, setNewLabelPreviewText] = useState<string>("")
  const [selectedColor, setSelectedColor] =
    useState<LabelThemeColors>(defaultSelectedColor)
  const [newLabelError, setNewLabelError] = useState<string>("")
  const addNewLabelInputRef = useRef<HTMLInputElement>(null)

  const handleBatchedRemoveLabels = (labelToRemove: LabelProps) => {
    // Remove the label from tempLabels
    const updatedLabels = tempLabels?.filter(
      (label: LabelProps) => label.pid !== labelToRemove.pid,
    )

    setTempLabels(updatedLabels)
  }

  useEffect(() => {
    if (newLabelError !== "" && newLabelPreviewText !== "") {
      // Reset error message if preview text changes
      setNewLabelError("")
    }
    // This useEffect() hook only needs to run when the preview text changes; validation happens on [Enter]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newLabelPreviewText])

  useEffect(() => {
    if (addNewLabelInputRef?.current && addNewLabelInputRef.current.value !== "") {
      // Reset input field text & selected color, needs to happen after the tempLabels setState for it to be reliable
      setNewLabelPreviewText("")
      setSelectedColor(defaultSelectedColor)

      addNewLabelInputRef.current.value = ""
      addNewLabelInputRef.current.focus()
    }
  }, [tempLabels, defaultSelectedColor])

  const handleNewLabelValidation = (newLabelValue: string) => {
    if (newLabelValue === "") {
      setNewLabelError("Label name cannot be empty")
    }

    const isLabelAlreadyCreated = orgLabels?.find(
      (label) => label.name === newLabelValue,
    )
    const isLabelTempCreated = tempLabels?.find((label) => label.name === newLabelValue)
    if (isLabelAlreadyCreated || isLabelTempCreated) {
      setNewLabelError("Label already exists")
    }

    // If label already exists or is empty, newLabelValue is invalid
    if (isLabelAlreadyCreated || isLabelTempCreated || newLabelValue === "") {
      return false
    }
    return true
  }

  const handleAddLabel = () => {
    const inputField = addNewLabelInputRef.current

    const newLabelText = inputField?.value
    if (!newLabelText) return

    const isValidName = handleNewLabelValidation(newLabelText)
    if (!isValidName) return

    const selectedColorKey = retrieveColorKey(
      (inputField?.getAttribute("data-selectedcolor") as LabelThemeColors) ||
        LabelThemeColors.SUCCESS,
    )

    if (selectedColorKey && newLabelText) {
      setTempLabels((prev) => [
        ...prev,
        {
          pid: newLabelText,
          name: newLabelText,
          themeColor: selectedColorKey as LabelColor,
        },
      ])
    }
  }

  const handleKeyDownAddLabel = (
    // Will capture "Enter" while popover is open, no matter the element in focus
    event: KeyboardEvent,
  ) => {
    if (event.key === "Enter" && addNewLabelInputRef?.current) {
      handleAddLabel()
    }
  }

  const handleSelectColor = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()

      const colorSelection = event.currentTarget as HTMLInputElement
      const clickedColor = colorSelection.value as LabelThemeColors
      if (!clickedColor) return

      setSelectedColor(clickedColor)
    },
    [setSelectedColor],
  )

  const addLabelDefaultValue = "Enter a new option"

  return (
    <>
      <div
        className="border-1 rounded-t border-b border-colors-border-secondary bg-grey-100 pt-2 pb-2 pl-4 pr-4"
        onKeyDown={(event) => handleKeyDownAddLabel(event)}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault()
          }}
        >
          <FormField id="newLabel" errorText={newLabelError} required>
            <Input
              type="text"
              placeholder={addLabelDefaultValue}
              className="border-0 bg-transparent p-0 hover:bg-grey-100"
              onChange={(event) => setNewLabelPreviewText(event.target.value)}
              ref={addNewLabelInputRef}
              variant="small"
              data-selectedcolor={selectedColor}
              isError={newLabelError !== ""}
              name="newLabel"
            />
            {helperText && (
              <p className="pt-1 text-caption text-grey-500">{helperText}</p>
            )}
          </FormField>
        </form>
        {hasSiteLabels && (
          <div className="flex flex-wrap gap-2 pt-4">
            {tempLabels.map((label: LabelProps) => (
              <Label
                pid={label.pid}
                key={label.name}
                name={label.name}
                themeColor={label.themeColor}
                className={`border border-${
                  radioItemDisplayColors[LabelThemeColors[label.themeColor]]
                }`}
                onClick={() => handleBatchedRemoveLabels(label)}
                iconPosition="right"
                icon={IconX}
              />
            ))}
          </div>
        )}
      </div>

      {newLabelPreviewText === "" ? (
        <div
          className={`rounded-b bg-white ${hasNoLabels ? "px-4 pt-4 pb-8" : ""}`}
        ></div>
      ) : (
        <div
          className="bg-white-300 mb-2 rounded-b"
          onKeyDown={(event) => handleKeyDownAddLabel(event)}
        >
          <div className="bg-grey-300 py-2 px-3">
            <div className="px-2">
              <div className="flex items-center gap-2">
                <p className="text-body2 text-colors-text-primary">Create</p>

                <Tag label={newLabelPreviewText} size="small" variant={selectedColor} />
              </div>
              <SelectLabelColor
                handleSelectColor={handleSelectColor}
                selectedColor={selectedColor}
              />
            </div>
            <Button
              onKeyDown={(event) => handleKeyDownAddLabel(event)}
              onClick={handleAddLabel}
              variant="neutral"
              className="mt-3 w-full"
              size="xsmall"
            >
              Create new label
            </Button>
          </div>
        </div>
      )}

      {hasOrgLabels && (
        <div className="p-4 pb-1">
          <p className="pb-4 text-body2">Select an option or create one</p>
          {
            <ExistingLabelsList
              orgLabels={orgLabels}
              tempLabels={tempLabels}
              setTempLabels={setTempLabels}
              removeLabel={removeLabel}
              editLabelColor={editLabelColor}
              editLabelName={editLabelName}
              deleteLabel={deleteLabel}
              labelsToShow="all"
              isBulkAction={isBulkAction}
            />
          }
        </div>
      )}

      {(hasSiteLabels || newLabelPreviewText !== "") && (
        <div
          className={`flex justify-center py-3 px-4 ${
            !hasOrgLabels
              ? ""
              : "border-1 rounded-b border-t border-colors-border-secondary"
          }`}
        >
          <Button size="small" className="w-full" onClick={handleSendBatchedUpdate}>
            Save changes
          </Button>
        </div>
      )}
    </>
  )
}
